import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useProfile from "hooks/useProfile";
import ProfileHeader from "./components/ProfileHeader";
import PersonalInfoSection from "./components/PersonalInfoSection";
import ContactInfoSection from "./components/ContactInfoSection";
import PasswordSecuritySection from "./components/PasswordSecuritySection";
import NotificationPreferencesSection from "./components/NotificationPreferencesSection";
import ActiveSessionsSection from "./components/ActiveSessionsSection";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import "./Profile.scss";
import Button from "Components/Common/Button/Button";

type Section = "profile" | "security" | "notifications" | "sessions";

const NAV_ITEMS: Array<{ id: Section; icon: string; label: string }> = [
  { id: "profile", icon: "👤", label: "Personal Info" },
  { id: "security", icon: "🔒", label: "Password & Security" },
  { id: "notifications", icon: "🔔", label: "Notifications" },
  { id: "sessions", icon: "💻", label: "Active Sessions" },
];

const ROLE_DASHBOARD: Record<string, string> = {
  admin: "/admin",
  gc: "/gc-dashboard",
  sub: "/lien-waiver",
  lender: "/bank-dashboard",
  owner: "/owner-dashboard",
  inspector: "/project-inspection",
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<Section>("profile");
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const role = localStorage.getItem("role") ?? "";
  const dashboardPath = ROLE_DASHBOARD[role] ?? "/";
  const {
    profile,
    loading,
    saving,
    changingPassword,
    sendingOTP,
    verifyingOTP,
    savingNotifications,
    uploadingAvatar,
    saveProfile,
    changePassword,
    revokeSession,
    revokeAllSessions,
    updatePhone,
    sendPhoneVerificationOTP,
    verifyPhoneOTP,
    updateNotificationChannels,
    uploadAvatar,
    refetchProfile,
  } = useProfile();
  return (
    <div className="profilePageWrap">
      {navOpen && (
        <div className="profileNavBackdrop" onClick={() => setNavOpen(false)} />
      )}

      <aside
        className={`sidebar profileNav${collapsed ? " collapsed" : ""}${navOpen ? " open" : ""}`}
      >
        <Button
          type="button"
          className="profileNavMenuBtn"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation"
        >
          ☰
        </Button>
        <div className="sidebarContent">
          <div className="sidebarGroup">
            <div className="sidebarSectionLabel">Account</div>
            {NAV_ITEMS.map((item) => (
              <Button
                key={item.id}
                type="button"
                className={`sidebarItem${activeSection === item.id ? " active" : ""}`}
                onClick={() => {
                  setActiveSection(item.id);
                  setNavOpen(false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                <span className="sidebarIcon">{item.icon}</span>
                <span className="sidebarText">{item.label}</span>
              </Button>
            ))}
          </div>
        </div>
      </aside>

      <div className="profileMainWrap">
        <div className="profileMain">
          {loading ? (
            <div className="profileLoading">
              <div className="profileLoadingHead">
                <Skeleton
                  variant="card"
                  width={72}
                  height={72}
                  borderRadius="50%"
                />
                <div className="profileLoadingHeadText">
                  <Skeleton width={160} height={20} borderRadius={6} />
                  <Skeleton width={120} height={14} borderRadius={6} />
                  <Skeleton width={90} height={14} borderRadius={6} />
                </div>
              </div>
              <Skeleton
                variant="card"
                width="100%"
                height={180}
                borderRadius={10}
                className="profileLoadingCard1"
              />
              <Skeleton
                variant="card"
                width="100%"
                height={220}
                borderRadius={10}
              />
            </div>
          ) : (
            <>
              {activeSection === "profile" && (
                <>
                  <ProfileHeader
                    profile={profile}
                    onUploadAvatar={uploadAvatar}
                    uploadingAvatar={uploadingAvatar}
                  />
                  <PersonalInfoSection
                    profile={profile}
                    onSave={saveProfile}
                    saving={saving}
                  />
                  <ContactInfoSection
                    profile={profile}
                    onSave={saveProfile}
                    saving={saving}
                    onUpdatePhone={updatePhone}
                    onSendOTP={sendPhoneVerificationOTP}
                    onVerifyOTP={verifyPhoneOTP}
                    onRefetchProfile={() => refetchProfile(true)}
                    sendingOTP={sendingOTP}
                    verifyingOTP={verifyingOTP}
                    smsNotificationsEnabled={profile.smsNotificationsEnabled}
                    onGoToNotifications={() =>
                      setActiveSection("notifications")
                    }
                  />
                </>
              )}

              {activeSection === "security" && (
                <PasswordSecuritySection
                  onChangePassword={changePassword}
                  onToggleSms2fa={(enabled) =>
                    saveProfile({ sms2faEnabled: enabled })
                  }
                  onToggleEmailAlert={(enabled) =>
                    saveProfile({ emailLoginAlertEnabled: enabled })
                  }
                  changing={changingPassword}
                  sms2faEnabled={profile.sms2faEnabled}
                  emailLoginAlertEnabled={profile.emailLoginAlertEnabled}
                  smsNotificationsEnabled={profile.smsNotificationsEnabled}
                />
              )}

              {activeSection === "notifications" && (
                <NotificationPreferencesSection
                  initialSmsEnabled={profile.smsNotificationsEnabled}
                  initialEmailEnabled={profile.emailNotificationsEnabled}
                  sms2faEnabled={profile.sms2faEnabled}
                  phone={profile.phone}
                  phoneVerified={profile.phoneVerified}
                  onUpdateChannels={updateNotificationChannels}
                  savingNotifications={savingNotifications}
                />
              )}

              {activeSection === "sessions" && (
                <ActiveSessionsSection
                  sessions={profile.activeSessions}
                  onRevoke={revokeSession}
                  onRevokeAll={revokeAllSessions}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
